import { Component } from '@theme/component';

/**
 * A custom element that renders a functional search input block
 * based on the Figma design that performs actual search functionality.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 *
 * @extends {Component<Refs>}
 */
class SearchBlockComponent extends Component {
  /**
   * Initialize the search block component
   */
  connectedCallback() {
    super.connectedCallback();
    
    // Add event listeners for search functionality
    const form = this.querySelector('.search-block-form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit);
    }
  }

  /**
   * Clean up event listeners
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    
    const form = this.querySelector('.search-block-form');
    if (form) {
      form.removeEventListener('submit', this.handleSubmit);
    }
  }

  /**
   * Handle form submission
   * @param {Event} event - The submit event
   */
  handleSubmit = (event) => {
    const input = this.refs.searchInput;
    if (!input || !input.value.trim()) {
      event.preventDefault();
      return;
    }
    // Form will submit naturally to the search URL
  };

  /**
   * Handle keyboard events on the search input
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const input = this.refs.searchInput;
    
    switch (event.key) {
      case 'Enter':
        if (input.value.trim()) {
          // Let the form submit naturally
          return;
        }
        event.preventDefault();
        break;
      case 'Escape':
        if (input.value) {
          input.value = '';
          this.updateResetButton();
        }
        break;
    }
  };

  /**
   * Handle focus events on the search input
   * @param {FocusEvent} event - The focus event
   */
  handleFocus = (event) => {
    // Optional: Add any focus-specific behavior here
    // For now, just ensure proper styling is applied via CSS
  };

  /**
   * Handle clear/reset button click
   * @param {Event} event - The click event
   */
  handleClearClick = (event) => {
    event.preventDefault();
    const input = this.refs.searchInput;
    if (input) {
      input.value = '';
      input.focus();
      this.updateResetButton();
    }
  };

  /**
   * Update the reset button visibility based on input content
   */
  updateResetButton() {
    const input = this.refs.searchInput;
    const resetButton = this.querySelector('.search-block__reset-button');
    
    if (input && resetButton) {
      if (input.value.trim()) {
        resetButton.removeAttribute('hidden');
      } else {
        resetButton.setAttribute('hidden', '');
      }
    }
  }

  /**
   * Handle input changes to show/hide reset button
   * @param {Event} event - The input event
   */
  handleInput = (event) => {
    this.updateResetButton();
  };
}

// Register the custom element if it hasn't been registered already
if (!customElements.get('search-block-component')) {
  customElements.define('search-block-component', SearchBlockComponent);
}

export default SearchBlockComponent;